(function () {
  const YT_API = 'https://www.youtube.com/iframe_api';
  const VIMEO_API = 'https://player.vimeo.com/api/player.js';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (document.querySelector('script[src="' + src + '"]')) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = function () {
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function waitForYT() {
    return new Promise(function (resolve) {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }
      const previous = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof previous === 'function') previous();
        resolve(window.YT);
      };
      loadScript(YT_API).catch(function () {});
    });
  }

  function waitForVimeo() {
    return loadScript(VIMEO_API).then(function () {
      return window.Vimeo;
    });
  }

  class AtlasVideoBanner {
    constructor(root) {
      this.root = root;
      this.playerEl = root.querySelector('[data-avb-player]');
      this.playBtn = root.querySelector('[data-avb-play]');
      this.muteBtn = root.querySelector('[data-avb-mute]');
      this.toggleLayer = root.querySelector('[data-avb-toggle]');
      this.type = root.dataset.videoType || '';
      this.videoId = root.dataset.videoId || '';
      this.videoSrc = root.dataset.videoSrc || '';
      this.player = null;
      this.isPlaying = false;
      this.isMuted = true;

      if (!this.playerEl || !this.type) return;

      this.bindUI();
      this.initPlayer();
    }

    bindUI() {
      if (this.toggleLayer) {
        this.toggleLayer.addEventListener('click', function (event) {
          if (event.target.closest('[data-avb-mute]')) return;
          this.togglePlay();
        }.bind(this));
      }

      if (this.muteBtn) {
        this.muteBtn.addEventListener('click', function (event) {
          event.stopPropagation();
          this.toggleMute();
        }.bind(this));
      }
    }

    setPlaying(playing) {
      this.isPlaying = playing;
      this.root.classList.toggle('is-playing', playing);
      this.root.classList.toggle('is-paused', !playing);
      if (playing) {
        this.root.classList.add('is-started');
      }
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
      }
    }

    setMuted(muted) {
      this.isMuted = muted;
      this.root.classList.toggle('is-unmuted', !muted);
      if (this.muteBtn) {
        this.muteBtn.setAttribute('aria-label', muted ? 'Unmute video' : 'Mute video');
      }
    }

    initPlayer() {
      if (this.type === 'native') {
        this.initNative();
      } else if (this.type === 'youtube') {
        this.initYouTube();
      } else if (this.type === 'vimeo') {
        this.initVimeo();
      }
    }

    initNative() {
      const video = document.createElement('video');
      video.src = this.videoSrc;
      video.muted = true;
      video.autoplay = true;
      video.playsInline = true;
      video.loop = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      this.playerEl.appendChild(video);
      this.player = video;

      const playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(function () {
            this.setPlaying(true);
          }.bind(this))
          .catch(function () {
            this.setPlaying(false);
          }.bind(this));
      } else {
        this.setPlaying(true);
      }

      this.setMuted(true);
    }

    initYouTube() {
      const holder = document.createElement('div');
      holder.id = 'avb-yt-' + this.root.dataset.sectionId;
      this.playerEl.appendChild(holder);

      waitForYT().then(function (YT) {
        this.player = new YT.Player(holder.id, {
          videoId: this.videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            mute: 1,
            controls: 0,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            loop: 1,
            playlist: this.videoId,
            fs: 0,
            iv_load_policy: 3,
            cc_load_policy: 0,
            disablekb: 1,
            enablejsapi: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: function (event) {
              event.target.mute();
              event.target.playVideo();
              this.setMuted(true);
              this.setPlaying(true);

              const iframe = event.target.getIframe && event.target.getIframe();
              if (iframe) {
                iframe.style.pointerEvents = 'none';
                iframe.setAttribute('tabindex', '-1');
              }
            }.bind(this),
            onStateChange: function (event) {
              if (event.data === YT.PlayerState.PLAYING) {
                this.setPlaying(true);
              } else if (
                event.data === YT.PlayerState.PAUSED ||
                event.data === YT.PlayerState.ENDED
              ) {
                this.setPlaying(false);
              }
            }.bind(this),
          },
        });
      }.bind(this));
    }

    initVimeo() {
      const iframe = document.createElement('iframe');
      iframe.src =
        'https://player.vimeo.com/video/' +
        this.videoId +
        '?autoplay=1&muted=1&controls=0&loop=1&playsinline=1&title=0&byline=0&portrait=0&sidedock=0';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.setAttribute('allowfullscreen', 'false');
      iframe.title = 'Video';
      this.playerEl.appendChild(iframe);

      iframe.style.pointerEvents = 'none';
      iframe.setAttribute('tabindex', '-1');

      waitForVimeo().then(function (Vimeo) {
        this.player = new Vimeo.Player(iframe);
        this.player.setMuted(true);
        this.player.play().then(function () {
          this.setPlaying(true);
        }.bind(this)).catch(function () {
          this.setPlaying(false);
        }.bind(this));
        this.setMuted(true);

        this.player.on('play', function () {
          this.setPlaying(true);
        }.bind(this));
        this.player.on('pause', function () {
          this.setPlaying(false);
        }.bind(this));
        this.player.on('ended', function () {
          this.setPlaying(false);
        }.bind(this));
      }.bind(this));
    }

    togglePlay() {
      if (!this.player) return;

      if (this.type === 'native') {
        if (this.player.paused) {
          this.player.play();
          this.setPlaying(true);
        } else {
          this.player.pause();
          this.setPlaying(false);
        }
        return;
      }

      if (this.type === 'youtube' && this.player.pauseVideo) {
        if (this.isPlaying) {
          this.player.pauseVideo();
          this.setPlaying(false);
        } else {
          this.player.playVideo();
          this.setPlaying(true);
        }
        return;
      }

      if (this.type === 'vimeo' && this.player.pause) {
        if (this.isPlaying) {
          this.player.pause();
        } else {
          this.player.play();
        }
      }
    }

    toggleMute() {
      if (!this.player) return;
      const nextMuted = !this.isMuted;

      if (this.type === 'native') {
        this.player.muted = nextMuted;
        this.setMuted(nextMuted);
        return;
      }

      if (this.type === 'youtube') {
        if (nextMuted && this.player.mute) {
          this.player.mute();
        } else if (!nextMuted && this.player.unMute) {
          this.player.unMute();
          this.player.setVolume(100);
        }
        this.setMuted(nextMuted);
        return;
      }

      if (this.type === 'vimeo' && this.player.setMuted) {
        this.player.setMuted(nextMuted).then(function () {
          this.setMuted(nextMuted);
        }.bind(this));
      }
    }
  }

  function initAll() {
    document.querySelectorAll('[data-atlas-video-banner]').forEach(function (root) {
      if (root.dataset.avbInit === 'true') return;
      root.dataset.avbInit = 'true';
      new AtlasVideoBanner(root);
    });
  }

  document.addEventListener('DOMContentLoaded', initAll);
  document.addEventListener('shopify:section:load', initAll);
})();
